import { useCallback, useMemo } from 'react'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IGeocodingAllowedCountryEnum, IGeocodingResultTypeEnum } from 'types/graphqlTypes'
import { useDebouncedCallback } from 'use-debounce/lib'
import { useLocationSearch, ILocationSearchQueryVariables } from '../useLocationSearch'
import { getMappedResults, TGetResultLabelFunc } from './utils/getMappedResults'

export interface IUseGeocodingSearchState {
  isLoading: boolean
  /**
   * @example [{ id: 'place.123', city: 'New York', ... }]
   */
  results: ILocationSuggestion[]
  /**
   * Fetches results from geocoding search API
   */
  getResults: (query: string, props?: ILocationSearchQueryVariables) => Promise<void>
}

export interface IUseGeocodingSearchProps {
  /**
   * Filter out unwanted searched items
   * @default () => false
   * @example searchString => ['Any Pickup', 'Any Dropoff'].incudes(term)
   */
  filterExcludedSearchString?: (searchString: string) => boolean
  /**
   * Debounce for query input search
   * @default 150
   */
  debounce?: number
  /**
   * @example ({ city, state }) => [city, state].join(', ')
   */
  getResultLabel?: TGetResultLabelFunc
  /**
   * @example [IGeocodingResultTypeEnum.City]
   */
  resultTypes?: IGeocodingResultTypeEnum[]
  /**
   * @example [IGeocodingAllowedCountryEnum.US]
   */
  countries?: IGeocodingAllowedCountryEnum[]
}

/**
 * Returns stateful REST API query
 * @note This is **only** used to support legacy `MapboxSuggestion` types
 * @example
 * ```
 * const { results = [], getResults } = useGeocodingSearch({ debounce: 200 })
 * ```
 */
const useGeocodingSearch = ({
  debounce: debounceProp = 150,
  filterExcludedSearchString: filterExcludedSearchStringProp = () => false,
  getResultLabel,
  resultTypes,
  countries,
}: IUseGeocodingSearchProps = {}): IUseGeocodingSearchState => {
  const [refetch, state] = useLocationSearch()
  const { data, loading } = state || {}
  const { geocodingSearch: mapResults = [] } = data || {}

  /**
   * @note For internal management only
   */
  const _getResultsWithoutDebounce: IUseGeocodingSearchState['getResults'] = useCallback(
    async (searchString: string = '', args = {}): Promise<void> => {
      if (filterExcludedSearchStringProp(searchString)) {
        return
      }

      refetch({
        variables: {
          searchString,
          resultTypes,
          countries,
          ...args,
        },
      })
    },
    [countries, filterExcludedSearchStringProp, refetch, resultTypes]
  )

  const [getResults] = useDebouncedCallback(_getResultsWithoutDebounce, debounceProp)

  const results = useMemo(() => {
    return getMappedResults(mapResults, { getResultLabel })
  }, [getResultLabel, mapResults])

  return {
    isLoading: loading,
    getResults,
    results,
  }
}

export default useGeocodingSearch
